import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { 
  Building2, MapPin, Phone, Globe, Clock, Save, 
  Facebook, Instagram, Youtube, Navigation, Map
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const AdminVenuePage = () => {
  const { token } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    name: '',
    address: '',
    contact_number: '',
    facebook_url: '',
    instagram_url: '',
    tiktok_url: '',
    youtube_url: '',
    waze_url: '',
    google_maps_url: '',
    weekday_open: '06:00',
    weekday_close: '22:00',
    weekend_open: '06:00',
    weekend_close: '22:00'
  });

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/venue-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching venue settings:', error);
      toast.error('Failed to load venue settings');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/venue-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Venue settings saved successfully');
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving venue settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Generate time options for select
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      const time24 = `${String(hour).padStart(2, '0')}:00`;
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const period = hour < 12 ? 'AM' : 'PM';
      options.push({ value: time24, label: `${hour12}:00 ${period}` });
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="h-8 bg-muted animate-pulse rounded w-1/4 mb-6"></div>
          <div className="grid gap-6">
            <div className="h-64 bg-muted animate-pulse rounded-xl"></div>
            <div className="h-64 bg-muted animate-pulse rounded-xl"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6" data-testid="admin-venue-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Venue Settings</h1>
            <p className={theme.textMuted}>Manage your venue information and operating hours</p>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="save-venue-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card className={`${theme.card} border ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${theme.text}`}>
                <Building2 className="h-5 w-5 text-emerald-500" />
                Basic Information
              </CardTitle>
              <CardDescription className={theme.textMuted}>
                Essential venue details shown to customers
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className={theme.text}>Venue Name</Label>
                <Input
                  value={settings.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter venue name"
                  className={`${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="venue-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${theme.text}`}>
                  <MapPin className="h-4 w-4" />
                  Address
                </Label>
                <Input
                  value={settings.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Enter venue address"
                  className={`${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="venue-address-input"
                />
              </div>

              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${theme.text}`}>
                  <Phone className="h-4 w-4" />
                  Contact Number
                </Label>
                <Input
                  value={settings.contact_number}
                  onChange={(e) => handleChange('contact_number', e.target.value)}
                  placeholder="Enter contact number"
                  className={`${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="venue-contact-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Operating Hours */}
          <Card className={`${theme.card} border ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${theme.text}`}>
                <Clock className="h-5 w-5 text-blue-500" />
                Operating Hours
              </CardTitle>
              <CardDescription className={theme.textMuted}>
                Set your venue's operating schedule
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Weekday Hours */}
              <div className="space-y-3">
                <Label className={`font-medium ${theme.text}`}>Weekdays (Mon - Fri)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={`text-sm ${theme.textMuted}`}>Opening Time</Label>
                    <select
                      value={settings.weekday_open}
                      onChange={(e) => handleChange('weekday_open', e.target.value)}
                      className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                      data-testid="weekday-open-select"
                    >
                      {timeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className={`text-sm ${theme.textMuted}`}>Closing Time</Label>
                    <select
                      value={settings.weekday_close}
                      onChange={(e) => handleChange('weekday_close', e.target.value)}
                      className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                      data-testid="weekday-close-select"
                    >
                      {timeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <Separator className={isDarkMode ? 'bg-slate-700' : ''} />

              {/* Weekend Hours */}
              <div className="space-y-3">
                <Label className={`font-medium ${theme.text}`}>Weekends (Sat - Sun)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={`text-sm ${theme.textMuted}`}>Opening Time</Label>
                    <select
                      value={settings.weekend_open}
                      onChange={(e) => handleChange('weekend_open', e.target.value)}
                      className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                      data-testid="weekend-open-select"
                    >
                      {timeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className={`text-sm ${theme.textMuted}`}>Closing Time</Label>
                    <select
                      value={settings.weekend_close}
                      onChange={(e) => handleChange('weekend_close', e.target.value)}
                      className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                      data-testid="weekend-close-select"
                    >
                      {timeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social Media Links */}
          <Card className={`${theme.card} border ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${theme.text}`}>
                <Globe className="h-5 w-5 text-purple-500" />
                Social Media Links
              </CardTitle>
              <CardDescription className={theme.textMuted}>
                Connect your social media profiles
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${theme.text}`}>
                  <Facebook className="h-4 w-4 text-blue-600" />
                  Facebook
                </Label>
                <Input
                  value={settings.facebook_url}
                  onChange={(e) => handleChange('facebook_url', e.target.value)}
                  placeholder="https://facebook.com/yourpage"
                  className={`${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="facebook-url-input"
                />
              </div>

              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${theme.text}`}>
                  <Instagram className="h-4 w-4 text-pink-500" />
                  Instagram
                </Label>
                <Input
                  value={settings.instagram_url}
                  onChange={(e) => handleChange('instagram_url', e.target.value)}
                  placeholder="https://instagram.com/yourpage"
                  className={`${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="instagram-url-input"
                />
              </div>

              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${theme.text}`}>
                  <TikTokIcon className="h-4 w-4" />
                  TikTok
                </Label>
                <Input
                  value={settings.tiktok_url}
                  onChange={(e) => handleChange('tiktok_url', e.target.value)}
                  placeholder="https://tiktok.com/@yourpage"
                  className={`${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="tiktok-url-input"
                />
              </div>

              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${theme.text}`}>
                  <Youtube className="h-4 w-4 text-red-500" />
                  YouTube
                </Label>
                <Input
                  value={settings.youtube_url}
                  onChange={(e) => handleChange('youtube_url', e.target.value)}
                  placeholder="https://youtube.com/@yourchannel"
                  className={`${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="youtube-url-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Map Links */}
          <Card className={`${theme.card} border ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <CardHeader>
              <CardTitle className={`flex items-center gap-2 ${theme.text}`}>
                <Map className="h-5 w-5 text-amber-500" />
                Navigation Links
              </CardTitle>
              <CardDescription className={theme.textMuted}>
                Help customers find your venue easily
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${theme.text}`}>
                  <Navigation className="h-4 w-4 text-cyan-500" />
                  Waze Link
                </Label>
                <Input
                  value={settings.waze_url}
                  onChange={(e) => handleChange('waze_url', e.target.value)}
                  placeholder="https://waze.com/ul/..."
                  className={`${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="waze-url-input"
                />
              </div>

              <div className="space-y-2">
                <Label className={`flex items-center gap-2 ${theme.text}`}>
                  <MapPin className="h-4 w-4 text-red-500" />
                  Google Maps Link
                </Label>
                <Input
                  value={settings.google_maps_url}
                  onChange={(e) => handleChange('google_maps_url', e.target.value)}
                  placeholder="https://maps.google.com/..."
                  className={`${isDarkMode ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="google-maps-url-input"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Save Button (Mobile) */}
        <div className="lg:hidden">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminVenuePage;
